import { useEffect, useState } from "react"
import { Check, Info, LoaderCircle, Plus, Sparkles, X } from "lucide-react"
import { toast } from "sonner"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { editorCardClass, editorDescriptionClass, editorHeadingClass, editorInputClass } from "@/components/editor/editor-styles"
import { AI_MODEL_OPTIONS } from "@/lib/ai-models"
import { api } from "@/lib/api"
import type { AiModelOption, ChatbotDetail, ChatbotSettings } from "@/lib/types"

const MAX_SUGGESTED_QUESTIONS = 6
const templates: Record<string, string> = {
  support: "Em cada resposta, cumprimente o visitante, identifique rapidamente o problema e ofereça a próxima ação com base no conteúdo autorizado. Quando não houver fonte autorizada, admita abertamente e indique canais ou passos concretos para o time interno.",
  sales: "Descubra a necessidade antes de recomendar, use somente o que está nas páginas autorizadas, nunca invente preços ou condições e conduza a conversa para o próximo passo de forma natural.",
  docs: "Estruture a resposta em passos numerados, cite as fontes relevantes e indique a documentação relacionada. Quando a dúvida não estiver coberta, admita abertamente.",
  ecommerce: "Ajude o visitante a encontrar produtos, comparar opções e concluir a compra. Use apenas o catálogo e as políticas autorizadas; nunca invente preço, estoque, prazo ou promoção.",
  onboarding: "Receba novos usuários com passos curtos e claros. Guie a configuração inicial, antecipe dúvidas comuns e indique onde encontrar ajuda adicional.",
  education: "Explique conceitos em linguagem simples, use exemplos concretos, quebre o assunto em passos e confirme o entendimento antes de avançar.",
  concise: "Responda em no máximo três frases, direto ao ponto, sem introduções nem despedidas, sempre baseado no conteúdo autorizado.",
  custom: "",
}
const templateLabels: Record<string, string> = {
  support: "Especialista prestativo em suporte ao cliente",
  sales: "Assistente de vendas conciso",
  docs: "Guia de documentação técnica",
  ecommerce: "Vendedor de e-commerce focado em conversão",
  onboarding: "Guia de onboarding de novos usuários",
  education: "Professor didático passo a passo",
  concise: "Respostas curtas e diretas",
  custom: "Começar do zero",
}

function ModelCard({ option, selected, onClick }: { option: AiModelOption; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`text-left rounded-2xl border p-4 transition ${selected ? "border-primary bg-primary/10" : "field-bg border-border hover:border-foreground/25 hover:bg-accent/50"}`}
    >
      <div className="flex items-start justify-between">
        <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-muted text-muted-foreground">
          <Sparkles className="size-4" />
        </span>
        {selected && (
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Check className="size-3" />
          </span>
        )}
      </div>
      <p className="mt-4 text-[14px] font-semibold text-foreground">{option.name}</p>
      <p className="muted mt-1 text-[11px] leading-4">{option.description}</p>
      <div className="muted mt-4 flex items-center justify-between text-[10px]">
        <span>{selected ? "Selecionado" : "Disponível"}</span>
        <span>{option.context}</span>
      </div>
      <p className="mt-1.5 text-[10px] font-semibold text-success">{option.usage}</p>
    </button>
  )
}

export function AiSection({ id, value, onChange, onGenerated, models, expectedSuggestedQuestions }: { id: string; value: ChatbotSettings; onChange: (next: ChatbotSettings) => void; onGenerated: (next: ChatbotDetail) => void; models?: AiModelOption[]; expectedSuggestedQuestions: string[] }) {
  const [generating, setGenerating] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [template, setTemplate] = useState("custom")
  const [aiView, setAiView] = useState("model")
  const [remoteModels, setRemoteModels] = useState<AiModelOption[]>(models?.length ? models : AI_MODEL_OPTIONS)
  const set = <K extends keyof ChatbotSettings>(key: K, next: ChatbotSettings[K]) => onChange({ ...value, [key]: next })
  const promptInvalid = value.systemPrompt.trim().length < 10
  const options = remoteModels.length ? remoteModels : models?.length ? models : AI_MODEL_OPTIONS
  useEffect(() => {
    let active = true
    api.getAiConfig(id)
      .then((config) => { if (active && config.models?.length) setRemoteModels(config.models) })
      .catch(() => undefined)
    return () => { active = false }
  }, [id])
  const selectedModel = options.find((option) => option.id === value.model) ?? options[0]

  function updateQuestion(index: number, text: string) { set("suggestedQuestions", value.suggestedQuestions.map((question, i) => i === index ? text : question)) }
  function removeQuestion(index: number) { set("suggestedQuestions", value.suggestedQuestions.filter((_, i) => i !== index)) }
  function addQuestion() { if (value.suggestedQuestions.length < MAX_SUGGESTED_QUESTIONS) set("suggestedQuestions", [...value.suggestedQuestions, ""]) }
  async function publishReviewedQuestions() {
    setPublishing(true)
    try {
      const updated = await api.updateChatbot(id, {
        suggestedQuestions: value.suggestedQuestions,
        expectedSuggestedQuestions,
        publishReviewedSuggestedQuestions: true,
      })
      onGenerated(updated)
      toast.success("Perguntas revisadas publicadas no chat.")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível publicar as perguntas.")
    } finally { setPublishing(false) }
  }
  async function generateQuestions() {
    setGenerating(true)
    try { onGenerated(await api.generateSuggestedQuestions(id)); toast.success("Perguntas sugeridas geradas a partir do conhecimento conectado.") }
    catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível gerar perguntas agora.") }
    finally { setGenerating(false) }
  }

  function chooseTemplate(next: string) {
    setTemplate(next)
    if (next !== "custom") set("systemPrompt", templates[next] ?? "")
  }

  return (
    <form className="space-y-5" onSubmit={(event) => event.preventDefault()}>
      <Tabs value={aiView} onValueChange={setAiView} className="space-y-4">
        <TabsList aria-label="Seções da configuração de IA" className="grid h-auto w-full grid-cols-3 gap-1 rounded-xl border border-border bg-card p-1">
          <TabsTrigger value="model" className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">Modelo</TabsTrigger>
          <TabsTrigger value="prompt" className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">Prompt</TabsTrigger>
          <TabsTrigger value="questions" className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">Perguntas</TabsTrigger>
        </TabsList>

        <TabsContent value="model" className="mt-0">
          <section className={editorCardClass}>
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className={editorHeadingClass}>Escolha um modelo de IA</h2>
            <p className={editorDescriptionClass}>Selecione o modelo que melhor atende às suas necessidades de qualidade e velocidade de resposta.</p>
          </div>
          <Badge className="rounded-full border border-success/20 bg-success/10 px-2.5 py-0.5 text-[10px] font-semibold text-success">
            {options.length} disponíveis
          </Badge>
        </div>
        <div className="grid gap-3 md:grid-cols-3">
          {options.map((option) => (
            <ModelCard key={option.id} option={option} selected={option.id === value.model} onClick={() => set("model", option.id as ChatbotSettings["model"])} />
          ))}
        </div>
        <div className="mt-4 rounded-lg border border-border bg-muted/40 p-3">
          <div className="flex items-center gap-2">
            <Info className="muted size-3.5" />
            <span className="text-xs font-semibold text-foreground">{selectedModel?.name ?? value.model} selecionado</span>
          </div>
          <p className="muted mt-1 text-xs leading-relaxed">{selectedModel?.description ?? "O modelo conectado usa as fontes de conhecimento autorizadas para respostas fundamentadas."}</p>
          <div className="mt-2.5 flex flex-wrap gap-1.5 text-[10px] muted">
            <span className="rounded border border-border bg-card px-2 py-0.5">{selectedModel?.context ?? "Contexto padrão"}</span>
            <span className="rounded border border-border bg-card px-2 py-0.5">Busca de conhecimento</span>
            <span className="rounded border border-border bg-card px-2 py-0.5">Streaming habilitado</span>
          </div>
        </div>
          </section>
        </TabsContent>

        <TabsContent value="prompt" className="mt-0">
          <section className={editorCardClass}>
        <div className="mb-4">
          <h2 className={editorHeadingClass}>Configuração do prompt</h2>
          <p className={editorDescriptionClass}>Dê ao seu assistente uma direção clara e uma voz consistente.</p>
        </div>
        <Label htmlFor="prompt-template" className="mb-1.5 block text-xs font-medium text-foreground">Modelo de prompt</Label>
        <Select value={template} onValueChange={chooseTemplate}>
          <SelectTrigger id="prompt-template" className={editorInputClass}>
            <SelectValue placeholder="Escolha um modelo" />
          </SelectTrigger>
          <SelectContent>
            {Object.keys(templateLabels).map((key) => <SelectItem key={key} value={key}>{templateLabels[key]}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="mt-4 flex items-center justify-between">
          <Label htmlFor="system-prompt" className="text-xs font-medium text-foreground">Instruções personalizadas</Label>
          <span className="muted text-[11px]">{value.systemPrompt.length.toLocaleString()} / 5.000</span>
        </div>
        <Textarea
          id="system-prompt"
          maxLength={5000}
          rows={6}
          value={value.systemPrompt}
          onChange={(event) => set("systemPrompt", event.target.value)}
          className="field-bg mt-2 min-h-[180px] w-full resize-y rounded-xl border border-border p-4 text-[13px] leading-6 outline-none transition focus:border-ring focus:ring-4 focus:ring-ring/10"
          placeholder="Descreva a função, o público e os limites deste assistente."
        />
        {promptInvalid && <p className="mt-1.5 text-[11px] text-destructive">Descreva o comportamento do assistente em pelo menos 10 caracteres.</p>}
        <details className="mt-5 rounded-xl border border-border bg-muted/20 p-4">
          <summary className="cursor-pointer text-[12px] font-semibold">Dicas e diretrizes de escrita</summary>
          <ul className="muted mt-3 list-disc space-y-2 pl-5 text-[12px] leading-5">
            <li>Defina primeiro a função e o público do assistente.</li>
            <li>Use instruções positivas e específicas em vez de regras vagas.</li>
            <li>Diga ao assistente quando citar fontes ou fazer perguntas de acompanhamento.</li>
          </ul>
        </details>
          </section>
          <section className={`${editorCardClass} mt-5`}>
            <div className="mb-4">
              <h2 className={editorHeadingClass}>Perguntas repetidas</h2>
              <p className={editorDescriptionClass}>Quando o visitante repete a mesma dúvida várias vezes, o assistente deixa de tentar responder e oferece um atendente humano (se disponível) ou sugere reformular a pergunta.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="repeat-question-limit" className="mb-1.5 block text-xs font-medium text-foreground">Repetições até acionar o aviso</Label>
                <Input
                  id="repeat-question-limit"
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={10}
                  step={1}
                  value={value.repeatQuestionLimit}
                  className={editorInputClass}
                  onChange={(event) => { if (event.target.value !== "") set("repeatQuestionLimit", Math.min(10, Math.max(0, Math.round(Number(event.target.value))))) }}
                />
                <p className="muted mt-1.5 text-[11px] leading-4">De 0 a 10. Use 0 para desativar. Conta a mensagem atual mais as repetições anteriores.</p>
              </div>
              <div>
                <Label htmlFor="repeat-question-window" className="mb-1.5 block text-xs font-medium text-foreground">Janela de tempo (minutos)</Label>
                <Input
                  id="repeat-question-window"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={1440}
                  step={1}
                  value={value.repeatQuestionWindowMinutes}
                  className={editorInputClass}
                  onChange={(event) => { if (event.target.value !== "") set("repeatQuestionWindowMinutes", Math.min(1440, Math.max(1, Math.round(Number(event.target.value))))) }}
                />
                <p className="muted mt-1.5 text-[11px] leading-4">De 1 a 1.440 minutos. Só repetições dentro desse período são contadas.</p>
              </div>
            </div>
          </section>
        </TabsContent>

        <TabsContent value="questions" className="mt-0">
          <section className={editorCardClass}>
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
          <div>
            <h2 className={editorHeadingClass}>Perguntas sugeridas</h2>
            <p className={editorDescriptionClass}>Perguntas exibidas no widget para ajudar visitantes a iniciar conversas. Perguntas antigas ou ligadas a arquivos internos ficam ocultas no chat público. Revise o texto abaixo e publique a lista para autorizar sua exibição aos visitantes.</p>
          </div>
          <Button type="button" size="sm" className="accent-bg flex h-10 shrink-0 items-center gap-2 rounded-xl px-3 text-xs font-semibold" disabled={generating || publishing} onClick={() => void generateQuestions()}>
            {generating ? <LoaderCircle className="size-3.5 animate-spin" /> : <Sparkles className="size-3.5" />}
            {generating ? "Gerando..." : "Gerar com IA"}
          </Button>
        </div>
        <div className="mt-4 rounded-xl border border-border p-3">
          <p className="text-xs text-muted-foreground">Ao publicar esta lista, você confirma que todas as perguntas abaixo podem ser exibidas publicamente. Isso não torna os arquivos de origem públicos.</p>
          <Button type="button" variant="outline" size="sm" className="mt-3" disabled={generating || publishing || !value.suggestedQuestions.length || value.suggestedQuestions.some((question) => question.trim().length < 3)} onClick={() => void publishReviewedQuestions()}>
            {publishing ? "Publicando..." : "Publicar perguntas revisadas"}
          </Button>
        </div>
        <div className="mt-4 space-y-2">
          {value.suggestedQuestions.map((question, index) => (
            <div key={index} className="field-bg flex items-center gap-3 rounded-xl border border-border p-3">
              <Sparkles className="size-3.5 shrink-0 text-muted-foreground" />
              <Input
                value={question}
                maxLength={140}
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-xs shadow-none focus-visible:ring-0"
                placeholder="Escreva uma pergunta sugerida..."
                onChange={(event) => updateQuestion(index, event.target.value)}
              />
              <button
                type="button"
                className="muted flex h-8 w-8 items-center justify-center rounded-lg transition hover:text-destructive"
                aria-label="Remover pergunta"
                onClick={() => removeQuestion(index)}
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="muted mt-4 flex h-10 items-center gap-2 rounded-xl border border-dashed border-border px-3 text-xs font-semibold hover:border-foreground/30 hover:text-foreground"
          onClick={addQuestion}
          disabled={value.suggestedQuestions.length >= MAX_SUGGESTED_QUESTIONS}
        >
          <Plus className="size-3.5" />Adicionar pergunta manualmente
        </Button>
          </section>
        </TabsContent>
      </Tabs>
    </form>
  )
}
